/**
 * Care Minutes Service
 * Manages care minutes tracking and compliance for Aged Care Act 2024
 */

import LocalStorageService from '../storage/LocalStorageService';
import { 
  createCareMinutes, 
  calculateCareMinutesFromRoster,
  checkCompliance,
  calculateANACCAdjustedTarget,
  aggregateCareMinutes
} from '../../data/models/CareMinutes';
import ANACCService from '../api/ANACCService';
import { careMinutesData } from '../../data/mockData';

class CareMinutesService {
  constructor() {
    this.storageKey = 'careMinutes';
    this.initializeMockData();
  }

  /**
   * Initialize mock data if storage is empty
   */
  initializeMockData() {
    try {
      // Try immediate initialization first
      this._doInitialize();
      
      // Also set a timeout as fallback in case data isn't ready yet
      setTimeout(() => {
        this._doInitialize();
      }, 500);
    } catch (error) {
      console.error('[CareMinutesService] Error initializing care minutes mock data:', error);
      // Retry after a delay
      setTimeout(() => {
        this._doInitialize();
      }, 1000);
    }
  }
  
  /**
   * Internal method to actually initialize the data
   */
  _doInitialize() {
    try {
      // Check if careMinutesData exists and is an array with data
      if (!careMinutesData) {
        console.warn('[CareMinutesService] careMinutesData is undefined, will retry...');
        return;
      }
      
      if (!Array.isArray(careMinutesData)) {
        console.error('[CareMinutesService] careMinutesData is not an array:', typeof careMinutesData);
        return;
      }
      
      if (careMinutesData.length === 0) {
        console.warn('[CareMinutesService] careMinutesData is empty array');
        return;
      }
      
      const existing = LocalStorageService.get(this.storageKey, []);
      
      // Only initialize if storage is empty (don't re-initialize every time)
      if (existing.length === 0) {
        console.log(`[CareMinutesService] Initializing ${careMinutesData.length} care minutes records...`);
        let loaded = 0;
        careMinutesData.forEach(cm => {
          try {
            LocalStorageService.addItem(this.storageKey, cm);
            loaded++;
          } catch (e) {
            console.warn(`[CareMinutesService] Could not add record:`, e);
          }
        });
        console.log(`[CareMinutesService] Successfully initialized ${loaded} of ${careMinutesData.length} care minutes records`);
      } else {
        console.log(`[CareMinutesService] Storage already has ${existing.length} records, skipping initialization`);
      }
    } catch (error) {
      console.error('[CareMinutesService] Error in _doInitialize:', error);
    }
  }
  
  /**
   * Force re-initialize mock data (clears existing and reloads)
   */
  async forceReinitialize() {
    try {
      console.log('[CareMinutesService] Force re-initializing care minutes data...');
      
      // Clear existing data
      const existing = LocalStorageService.get(this.storageKey, []);
      console.log(`[CareMinutesService] Clearing ${existing.length} existing records...`);
      existing.forEach(item => {
        try {
          LocalStorageService.deleteItem(this.storageKey, item.id);
        } catch (e) {
          // Ignore deletion errors
        }
      });
      
      // Wait a moment for cleanup
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Check if careMinutesData exists and is an array with data
      if (!careMinutesData) {
        console.error('[CareMinutesService] careMinutesData is undefined - trying to import...');
        // Try dynamic import as fallback
        try {
          const mockData = await import('../../data/mockData');
          if (mockData.careMinutesData && Array.isArray(mockData.careMinutesData) && mockData.careMinutesData.length > 0) {
            console.log(`[CareMinutesService] Using dynamically imported data: ${mockData.careMinutesData.length} records`);
            return await this._loadData(mockData.careMinutesData);
          }
        } catch (importError) {
          console.error('[CareMinutesService] Dynamic import failed:', importError);
        }
        return { success: false, error: 'careMinutesData is undefined' };
      }
      
      return await this._loadData(careMinutesData);
    } catch (error) {
      console.error('[CareMinutesService] Error force re-initializing:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Internal method to load data into storage
   */
  async _loadData(data) {
    if (!Array.isArray(data)) {
      console.error('[CareMinutesService] Data is not an array:', typeof data);
      return { success: false, error: 'Data is not an array' };
    }
    
    if (data.length === 0) {
      console.warn('[CareMinutesService] Data is empty array');
      return { success: false, error: 'Data is empty' };
    }
    
    // Populate with fresh data
    console.log(`[CareMinutesService] Loading ${data.length} care minutes records...`);
    let loaded = 0;
    data.forEach((cm, index) => {
      try {
        if (!cm || !cm.id) {
          console.warn(`[CareMinutesService] Skipping invalid record at index ${index}`);
          return;
        }
        LocalStorageService.addItem(this.storageKey, cm);
        loaded++;
        if (loaded % 100 === 0) {
          console.log(`[CareMinutesService] Loaded ${loaded} of ${data.length} records...`);
        }
      } catch (e) {
        console.warn(`[CareMinutesService] Could not add item ${index}:`, e);
      }
    });
    
    console.log(`[CareMinutesService] Successfully loaded ${loaded} of ${data.length} care minutes records`);
    return { success: true, count: loaded, total: data.length };
  }

  /**
   * Get care minutes for a resident on a specific date
   */
  async getByResidentAndDate(residentId, date) {
    try {
      const all = LocalStorageService.get(this.storageKey, []);
      const found = all.find(
        cm => cm.residentId === residentId && cm.date === date
      );
      
      if (found) {
        return { success: true, data: found };
      }
      
      return { success: false, error: 'Care minutes not found' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get all care minutes for a resident
   */
  async getByResidentId(residentId, startDate = null, endDate = null) {
    try {
      const all = LocalStorageService.get(this.storageKey, []);
      let filtered = all.filter(cm => cm.residentId === residentId);
      
      if (startDate) {
        filtered = filtered.filter(cm => cm.date >= startDate);
      }
      
      if (endDate) {
        filtered = filtered.filter(cm => cm.date <= endDate);
      }
      
      return { success: true, data: filtered.sort((a, b) => a.date.localeCompare(b.date)) };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get care minutes for a facility
   */
  async getByFacilityId(facilityId, date = null) {
    try {
      const all = LocalStorageService.get(this.storageKey, []);
      let filtered = all.filter(cm => cm.facilityId === facilityId);
      
      if (date) {
        filtered = filtered.filter(cm => cm.date === date);
      }
      
      return { success: true, data: filtered };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Calculate and save care minutes from roster
   */
  async calculateFromRoster(residentId, facilityId, date, rosterEntries) {
    try {
      // Get AN-ACC classification for target adjustment
      const anaccResult = await ANACCService.getByResidentId(residentId);
      const anaccClass = anaccResult.success && anaccResult.data 
        ? anaccResult.data.anaccClass 
        : null;
      
      // Calculate minutes from roster
      const calculated = calculateCareMinutesFromRoster(rosterEntries, residentId, date);
      
      // Calculate AN-ACC adjusted target
      const baseTarget = 215;
      const targetRN = 44;
      const anaccAdjustedTarget = anaccClass 
        ? calculateANACCAdjustedTarget(anaccClass, baseTarget)
        : baseTarget;
      
      // Check compliance
      const compliance = checkCompliance(calculated, anaccAdjustedTarget, targetRN);
      
      // Create care minutes record
      const careMinutes = createCareMinutes({
        residentId,
        facilityId,
        date,
        minutes: calculated.minutes,
        totalMinutes: calculated.totalMinutes,
        rnMinutes: calculated.rnMinutes,
        targetMinutes: anaccAdjustedTarget,
        targetRNMinutes: targetRN,
        anaccClass,
        anaccAdjustedTarget,
        compliant: compliance.compliant,
        rnCompliant: compliance.rnCompliant,
        complianceGap: compliance.complianceGap,
        rnComplianceGap: compliance.rnComplianceGap,
        calculatedFromRoster: true,
        source: 'roster'
      });
      
      // Check if record exists for this date
      const existing = await this.getByResidentAndDate(residentId, date);
      if (existing.success) {
        // Update existing
        const updated = LocalStorageService.updateItem(
          this.storageKey,
          existing.data.id,
          { ...careMinutes, id: existing.data.id }
        );
        return { success: true, data: updated };
      } else {
        // Create new
        const created = LocalStorageService.addItem(this.storageKey, careMinutes);
        return { success: true, data: created };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Manually record care minutes
   */
  async recordManually(careMinutesData) {
    try {
      const anaccClass = careMinutesData.anaccClass || null;
      const baseTarget = 215;
      const targetRN = 44;
      const anaccAdjustedTarget = anaccClass 
        ? calculateANACCAdjustedTarget(anaccClass, baseTarget)
        : baseTarget;
      
      const totalMinutes = Object.values(careMinutesData.minutes || {}).reduce((sum, val) => sum + val, 0);
      const rnMinutes = careMinutesData.minutes?.rn || 0;
      
      const compliance = checkCompliance(
        { totalMinutes, rnMinutes },
        anaccAdjustedTarget,
        targetRN
      );
      
      const careMinutes = createCareMinutes({
        ...careMinutesData,
        totalMinutes,
        rnMinutes,
        targetMinutes: anaccAdjustedTarget,
        targetRNMinutes: targetRN,
        anaccAdjustedTarget,
        compliant: compliance.compliant,
        rnCompliant: compliance.rnCompliant,
        complianceGap: compliance.complianceGap,
        rnComplianceGap: compliance.rnComplianceGap,
        source: 'manual'
      });
      
      const existing = await this.getByResidentAndDate(
        careMinutes.residentId,
        careMinutes.date
      );
      
      if (existing.success) {
        const updated = LocalStorageService.updateItem(
          this.storageKey,
          existing.data.id,
          { ...careMinutes, id: existing.data.id }
        );
        return { success: true, data: updated };
      } else {
        const created = LocalStorageService.addItem(this.storageKey, careMinutes);
        return { success: true, data: created };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get facility-wide compliance summary
   */
  async getFacilityComplianceSummary(facilityId, date = null) {
    try {
      const targetDate = date || new Date().toISOString().split('T')[0];
      const result = await this.getByFacilityId(facilityId, targetDate);
      
      if (!result.success) {
        return { success: false, error: 'No data found' };
      }
      
      const careMinutes = result.data;
      const totalResidents = careMinutes.length;
      const compliantResidents = careMinutes.filter(cm => cm.compliant).length;
      const rnCompliantResidents = careMinutes.filter(cm => cm.rnCompliant).length;
      const fullyCompliantResidents = careMinutes.filter(cm => cm.compliant && cm.rnCompliant).length;
      
      const aggregated = aggregateCareMinutes(careMinutes);
      
      return {
        success: true,
        data: {
          date: targetDate,
          facilityId,
          totalResidents,
          compliantResidents,
          rnCompliantResidents,
          fullyCompliantResidents,
          complianceRate: totalResidents > 0 ? (fullyCompliantResidents / totalResidents) * 100 : 0,
          averageMinutes: aggregated.averageMinutes,
          averageRNMinutes: aggregated.averageRNMinutes,
          totalMinutes: aggregated.totalMinutes,
          totalRNMinutes: aggregated.rnMinutes,
          targetMinutes: aggregated.targetMinutes,
          targetRNMinutes: aggregated.targetRNMinutes,
          aggregated
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get compliance alerts (residents/facilities at risk)
   */
  async getComplianceAlerts(facilityId, date = null) {
    try {
      const targetDate = date || new Date().toISOString().split('T')[0];
      const result = await this.getByFacilityId(facilityId, targetDate);
      
      if (!result.success) {
        return { success: true, data: [] };
      }
      
      const alerts = result.data
        .filter(cm => !cm.compliant || !cm.rnCompliant)
        .map(cm => ({
          residentId: cm.residentId,
          date: cm.date,
          compliant: cm.compliant,
          rnCompliant: cm.rnCompliant,
          complianceGap: cm.complianceGap,
          rnComplianceGap: cm.rnComplianceGap,
          severity: cm.complianceGap > 30 || cm.rnComplianceGap > 10 ? 'high' : 'medium',
          message: `Resident ${cm.residentId} is ${cm.complianceGap} minutes short of target (RN: ${cm.rnComplianceGap} minutes short)`
        }));
      
      return { success: true, data: alerts };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get quarterly data for QFR submission
   */
  async getQuarterlyData(facilityId, quarter, year) {
    try {
      // Calculate quarter date range
      const quarterStart = new Date(year, (quarter - 1) * 3, 1);
      const quarterEnd = new Date(year, quarter * 3, 0);
      
      const startDate = quarterStart.toISOString().split('T')[0];
      const endDate = quarterEnd.toISOString().split('T')[0];
      
      const all = LocalStorageService.get(this.storageKey, []);
      const filtered = all.filter(
        cm => cm.facilityId === facilityId && 
              cm.date >= startDate && 
              cm.date <= endDate
      );
      
      // Group by AN-ACC class
      const byClass = {};
      filtered.forEach(cm => {
        const classKey = cm.anaccClass || 'unknown';
        if (!byClass[classKey]) {
          byClass[classKey] = [];
        }
        byClass[classKey].push(cm);
      });
      
      // Aggregate by class
      const aggregatedByClass = {};
      Object.keys(byClass).forEach(classKey => {
        aggregatedByClass[classKey] = aggregateCareMinutes(byClass[classKey]);
      });
      
      return {
        success: true,
        data: {
          facilityId,
          quarter,
          year,
          startDate,
          endDate,
          totalDays: filtered.length,
          aggregated: aggregateCareMinutes(filtered),
          byANACCClass: aggregatedByClass
        }
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

export default new CareMinutesService();
